import { useEffect, useMemo, useState } from 'react'
import { assignQuestionToProject, getProjects } from '../api/client'
import type { Project } from '../types'
import { useUIStore } from '../store/uiStore'
import './AssignProjectModal.css'

export default function AssignProjectModal({
  questionId,
  onAssigned,
}: {
  questionId: string | null | undefined
  onAssigned?: () => void
}) {
  const isOpen = useUIStore((s) => s.modals.assignProject)
  const closeModal = useUIStore((s) => s.closeModal)
  const openModal = useUIStore((s) => s.openModal)

  const [projects, setProjects] = useState<Project[]>([])
  const [selected, setSelected] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  const canSubmit = Boolean(questionId && selected)

  const load = async () => {
    setIsLoading(true)
    try {
      const rows = await getProjects()
      setProjects(rows)
    } catch (e) {
      console.error('Failed to load projects:', e)
      alert(`Failed to load projects: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const sorted = useMemo(() => {
    return [...projects].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
  }, [projects])

  if (!isOpen) return null

  const handleClose = () => {
    closeModal('assignProject')
    setSelected('')
  }

  const handleAssign = async () => {
    if (!questionId || !selected) return
    setIsLoading(true)
    try {
      await assignQuestionToProject(questionId, selected)
      closeModal('assignProject')
      onAssigned?.()
    } catch (e) {
      console.error('Failed to assign question to project:', e)
      alert(`Failed to assign: ${e instanceof Error ? e.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content assign-project-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add to project</h2>
          <button className="modal-close" onClick={handleClose} type="button">
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="ap-row">
            <label>Choose a project</label>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} disabled={isLoading}>
              <option value="">Select…</option>
              {sorted.map((p) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.name} ({p.question_count ?? 0} questions)
                </option>
              ))}
            </select>
          </div>

          <div className="ap-actions">
            <button
              className="btn-secondary"
              type="button"
              onClick={() => {
                closeModal('assignProject')
                openModal('newProject')
              }}
            >
              ＋ New project
            </button>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-primary" type="button" onClick={handleAssign} disabled={!canSubmit || isLoading}>
            {isLoading ? 'Adding…' : 'Add'}
          </button>
          <button className="btn-secondary" type="button" onClick={handleClose} disabled={isLoading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}



